import React, { useEffect, useRef, useState } from "react";
import { Search, FolderOpen, X, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";

export function DossierPill({ d, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-norea-primary/30 bg-norea-primary/10 text-norea-primary text-[11px] font-semibold px-2 py-0.5 max-w-full" data-testid={`dossier-pill-${d.id}`}>
      <FolderOpen className="w-3 h-3 flex-shrink-0" />
      <Link to={`/dossiers/${d.id}`} onClick={(e) => e.stopPropagation()} className="truncate hover:underline">{d.label}</Link>
      {onRemove && <button onClick={onRemove} className="opacity-60 hover:opacity-100" data-testid={`dossier-unlink-${d.id}`}><X className="w-3 h-3" /></button>}
    </span>
  );
}

export function DossierLinker({ basePath, ticketId, initial, onChange }) {
  const [linked, setLinked] = useState(initial || []);
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  useEffect(() => setLinked(initial || []), [initial]);
  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setRes([]); return; }
    timer.current = setTimeout(() => {
      setBusy(true);
      api.get("/dossier-search", { params: { q } }).then((r) => setRes(r.data.items || [])).catch(() => setRes([])).finally(() => setBusy(false));
    }, 200);
  }, [q]);
  const save = async (next) => {
    if (!ticketId) { setLinked(next); onChange?.(next); return; }
    try {
      const { data } = await api.put(`${basePath}/${ticketId}/dossiers`, { dossier_ids: next.map((d) => d.id) });
      setLinked(data.dossiers);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Association impossible");
    }
  };
  const add = (d) => { setQ(""); setOpen(false); if (!linked.some((x) => x.id === d.id)) save([...linked, d]); };

  return (
    <div data-testid="dossier-linker">
      <div className="text-xs font-semibold text-norea-textMuted mb-1.5">Dossiers clients {ticketId && <span className="font-normal">· enregistré automatiquement</span>}</div>
      {linked.length > 0 && <div className="flex flex-wrap gap-1.5 mb-2">{linked.map((d) => <DossierPill key={d.id} d={d} onRemove={() => save(linked.filter((x) => x.id !== d.id))} />)}</div>}
      <div className="relative max-w-md">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-norea-textDisabled" />
        <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Nom ou nom prénom du client…" className="w-full pl-8 pr-8 py-1.5 text-sm border border-norea-border rounded-md focus:ring-1 focus:ring-norea-primary outline-none bg-white" data-testid="dossier-search-input" />
        {busy && <Loader2 className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin text-norea-textMuted" />}
        {open && q.trim().length >= 2 && !busy && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-norea-border rounded-md shadow-lg max-h-64 overflow-y-auto" data-testid="dossier-search-results">
            {res.length === 0 ? <div className="px-3 py-2 text-xs text-norea-textMuted italic">Aucun dossier trouvé</div> : res.map((d) => {
              const already = linked.some((x) => x.id === d.id);
              return (
                <button key={d.id} onMouseDown={(e) => e.preventDefault()} onClick={() => add(d)} disabled={already}
                  className="w-full text-left px-3 py-2 hover:bg-norea-surfaceHover disabled:opacity-50 flex items-center gap-2" data-testid={`dossier-result-${d.id}`}>
                  <FolderOpen className="w-4 h-4 text-norea-primary flex-shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium truncate">{d.label}</span>
                    <span className="block text-[11px] text-norea-textMuted truncate">{[d.ville, d.produit, d.status].filter(Boolean).join(" · ")}</span>
                  </span>
                  {already && <span className="text-[10px] text-norea-textMuted">déjà associé</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
